/**
 * Tenant State Management
 *
 * This state is NOT persisted to avoid stale data issues.
 * Tenant data is synced from URL params and auth.state.tenants (fresh from /me API)
 * via TenantContext in ProtectedRoute/RootLayout
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import useAuthState, { Tenant } from './auth.state';
import { env } from '@/utils/constants/env';
import { axiosInstance } from '@/utils/lib/axios';
import { toast } from 'react-toastify';

type TenantStore = {
  currentTenant: Tenant | null;
  setCurrentTenant: (tenant: Tenant | null) => void;
  removeCurrentTenant: () => void;
  createNewWorkspace: (name: string) => void;
  homepageCreateNewWorkspace: boolean;
  setHomepageCreateNewWorkspace: (value: boolean) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
};

const useTenantState = create<TenantStore>()(
  persist(
    set => ({
      currentTenant: null,
      isLoading: false,
      homepageCreateNewWorkspace: false,

      setCurrentTenant: (tenant: Tenant | null) => set({ currentTenant: tenant }),
      removeCurrentTenant: () => set({ currentTenant: null }),
      setIsLoading: (loading: boolean) => set({ isLoading: loading }),

      createNewWorkspace: (name: string) => {
        const payload = { name };
        axiosInstance
          .post(`${env.apiTasklistBaseUrl}/api/v1/tenant/tenants`, payload)
          .then(response => {
            console.log('Workspace created:', response.data);
            const { loadUserProfile, token } = useAuthState.getState();
            if (token) loadUserProfile(token, true);
            set({ homepageCreateNewWorkspace: false });
            toast.success('Workspace created successfully', { position: 'top-right' });
          })
          .catch(error => {
            console.error('Error creating workspace:', error);
          });
      },

      setHomepageCreateNewWorkspace: (value: boolean) => set({ homepageCreateNewWorkspace: value }),
    }),
    {
      name: 'workspace-creation-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        // Only persist workspace creation state, NOT currentTenant
        homepageCreateNewWorkspace: state.homepageCreateNewWorkspace,
      }),
    },
  ),
);

export default useTenantState;
